/*
 * Verano Design : composition d'un bar sur la fiche d'un module (snippets/composition-bar.liquid).
 *
 * Le bloc porte en JSON les modules de la gamme (variantes, prix HT, images, plateaux, options),
 * les paliers de l'offre de composition et les options réglées « une par bar ». Tout se calcule
 * ici : prix TTC (HT pour un pro, data-taux à 0), remise du palier sur les modules seuls (comme
 * les codes COMPTOIR, qui ne portent pas sur les suppléments), plateau et options.
 *
 * Ajout au panier : une ligne par module avec les mêmes propriétés que depuis la fiche (choix du
 * plateau, teinte et code, « _Supplément <option> »), les suppléments dans le même envoi, puis le
 * code du palier atteint. Chaque option payante a un compteur, de 0 au nombre de modules qui la
 * proposent : elle est portée par autant de pièces, une ligne étant coupée en deux si besoin, pour
 * que verano.js (partie 5) compte autant de suppléments. Le devis reçoit les mêmes lignes (window.veranoDevis, assets/verano.js).
 */
(() => {
  const bloc = document.querySelector('[data-compo-bar]');
  if (!bloc) return;

  let donnees;
  try {
    donnees = JSON.parse(bloc.querySelector('[data-compo-donnees]').textContent);
  } catch (error) {
    console.log(error);
    return;
  }

  const modules = donnees.modules || [];
  const paliers = (donnees.paliers || [])
    .map((palier) => ({ n: parseInt(palier.seuil, 10), pct: parseFloat(palier.remise), code: palier.code || '' }))
    .filter((palier) => palier.n > 0 && palier.pct > 0)
    .sort((a, b) => a.n - b.n);
  const parBar = donnees.parBar || [];
  const taux = parseFloat(bloc.dataset.taux) || 0;
  const tauxReel = parseInt(bloc.dataset.tauxReel, 10) || 0;
  const RE_COULEUR = /couleur|colou?r|farbe|kleur|^cor\b|colore/i;

  const $ = (selecteur, racine = bloc) => racine.querySelector(selecteur);
  const $$ = (selecteur, racine = bloc) => [...racine.querySelectorAll(selecteur)];
  const argent = (centimes) => theme.Currency.formatMoney(Math.round(centimes), theme.settings.moneyFormat);
  // Même arrondi que snippets/ttc-amount.liquid.
  const ttc = (ht) => Math.floor(Math.round(ht * (100 + taux)) / 100);
  // Textes traduits (verano.bar.* des locales), repli en français ; [nom] remplacé par sa valeur.
  const textes = donnees.textes || {};
  const t = (cle, repli, valeurs = {}) => (textes[cle] || repli).replace(/\[(\w+)\]/g, (marque, nom) => (nom in valeurs ? valeurs[nom] : marque));
  const nbModules = (n) => (n > 1 ? t('modules_n', '[n] modules', { n }) : t('modules_un', '[n] module', { n }));
  const echapper = (texte) => String(texte ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* État */

  const etat = {
    choix: {},
    q: {},
    plateau: null,
    teintes: {},
    cases: {}
  };
  (donnees.options || []).forEach((option) => { etat.choix[option.nom] = option.choisie; });
  modules.forEach((module) => { etat.q[module.id] = module.actuel ? 1 : 0; });

  const varianteDe = (module, choix = etat.choix) => module.variantes.find((variante) => module.options.every((nom, i) => choix[nom] === undefined || variante.o[i] === choix[nom]));
  const modulesPris = () => modules.filter((module) => etat.q[module.id] > 0 && varianteDe(module));
  const nombre = () => modulesPris().reduce((total, module) => total + etat.q[module.id], 0);
  const palierDe = (n) => [...paliers].reverse().find((palier) => n >= palier.n);

  // Plateaux de la gamme, dans l'ordre de la fiche affichée puis des autres modules.
  const plateaux = [];
  [...modules].sort((a, b) => b.actuel - a.actuel).forEach((module) => {
    (module.plateau?.choix || []).forEach((choix) => {
      if (!plateaux.some((plateau) => plateau.libelle === choix.libelle)) plateaux.push({ libelle: choix.libelle, nuancier: choix.nuancier, note: choix.note });
    });
  });
  const modulePlateau = modules.find((module) => module.actuel && module.plateau) || modules.find((module) => module.plateau);
  if (plateaux.length) etat.plateau = (modulePlateau?.plateau.choix[0] || plateaux[0]).libelle;
  const choixPlateau = (module) => module.plateau?.choix.find((choix) => choix.libelle === etat.plateau);

  // Options à cocher de la gamme.
  const cases = [];
  modules.forEach((module) => module.cases.forEach((option) => {
    if (!cases.some((existante) => existante.nom === option.nom)) cases.push(option);
  }));
  cases.forEach((option) => { etat.cases[option.nom] = 0; });

  // Pièces du bar qui proposent l'option : c'est le maximum du compteur (un kit ou un pack par module).
  const piecesPour = (lignes, nom) => lignes.filter((ligne) => ligne.module.cases.some((x) => x.nom === nom)).reduce((total, ligne) => total + ligne.q, 0);

  // Teinte de départ de chaque nuancier : la première.
  $$('[data-compo-nuancier]').forEach((nuancier) => {
    const premiere = $('[data-compo-teinte]', nuancier);
    if (premiere) etat.teintes[nuancier.dataset.compoNuancier] = { teinte: premiere.dataset.compoTeinte, code: premiere.dataset.code, mention: premiere.dataset.mention };
  });

  /* Calculs */

  const calcul = () => {
    const n = nombre();
    const palier = palierDe(n);
    const pct = palier ? palier.pct : 0;
    const lignes = modulesPris().map((module) => {
      const variante = varianteDe(module);
      return { module, variante, q: etat.q[module.id], unite: ttc(variante.p) };
    });
    const sousTotal = lignes.reduce((total, ligne) => total + ligne.q * ligne.unite, 0);
    const remise = Math.round(sousTotal * pct / 100);

    const options = [];
    let plateauTotal = 0;
    lignes.forEach((ligne) => {
      const choix = choixPlateau(ligne.module);
      if (choix?.supp) plateauTotal += ligne.q * ttc(choix.p);
    });
    if (plateauTotal > 0) options.push({ libelle: `${modulePlateau?.plateau.nom || t('plateau', 'Plateau')} ${etat.plateau} × ${n}`, montant: plateauTotal });

    // Un module retiré : le compteur redescend au nombre de pièces restantes.
    cases.forEach((option) => {
      etat.cases[option.nom] = Math.min(etat.cases[option.nom], piecesPour(lignes, option.nom));
      const nombre = etat.cases[option.nom];
      if (nombre) options.push({ libelle: `${option.nom} × ${nombre}`, montant: nombre * ttc(option.p) });
    });
    const optionsTotal = options.reduce((total, option) => total + option.montant, 0);
    return { n, palier, pct, lignes, sousTotal, remise, options, total: sousTotal - remise + optionsTotal };
  };

  /* Affichage */

  const valeurPossible = (nomOption, valeur) => modules.some((module) => {
    const index = module.options.indexOf(nomOption);
    if (index < 0) return false;
    return module.variantes.some((variante) => variante.o[index] === valeur && module.options.every((nom, i) => nom === nomOption || etat.choix[nom] === undefined || RE_COULEUR.test(nomOption) === false || variante.o[i] === etat.choix[nom]));
  });

  // Liste déroulante au format des fiches (snippets/options-choix.liquid).
  const ICONE_LISTE = '<svg class="sibling-select__icon absolute pointer-events-none" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false"><path d="M10.884 4.323a1.25 1.25 0 0 0-1.768 0l-2.646 2.647a.75.75 0 0 0 1.06 1.06l2.47-2.47 2.47 2.47a.75.75 0 1 0 1.06-1.06l-2.646-2.647Z"/><path d="m13.53 13.03-2.646 2.647a1.25 1.25 0 0 1-1.768 0l-2.646-2.647a.75.75 0 0 1 1.06-1.06l2.47 2.47 2.47-2.47a.75.75 0 0 1 1.06 1.06Z"/></svg>';
  const liste = (id, attributs, choix) => `<div class="field variant-wrapper"><select id="${id}" class="select" is="custom-select" ${attributs}>${choix.map((x) => `<option value="${echapper(x.valeur)}"${x.choisi ? ' selected' : ''}>${echapper(x.texte)}</option>`).join('')}</select>${ICONE_LISTE}</div>`;

  const afficherOptionsVariantes = () => {
    const conteneur = $('[data-compo-options-variantes]');
    conteneur.innerHTML = (donnees.options || []).map((option) => {
      const couleur = RE_COULEUR.test(option.nom);
      const valeurs = option.valeurs.filter((valeur) => valeurPossible(option.nom, valeur.v));
      if (valeurs.length < 2 && !couleur) return '';
      const boutons = valeurs.map((valeur) => {
        const choisie = etat.choix[option.nom] === valeur.v;
        if (couleur) {
          // Pastille laquée quand la finition le demande (conditions de snippets/pastille-option.liquid).
          const condition = (valeur.k || []).find((c) => c.v.includes(etat.choix[donnees.options[c.p - 1]?.nom]));
          const image = condition?.i || valeur.i;
          const fond = image ? `background-image:url(${image})` : `background-color:${valeur.c || '#d4d4d4'}`;
          return `<button type="button" class="compo-bar__pastille${choisie ? ' is-choisie' : ''}" style="${fond}" title="${echapper(valeur.v)}" aria-label="${echapper(valeur.v)}" aria-pressed="${choisie}" data-compo-choix="${echapper(option.nom)}" data-valeur="${echapper(valeur.v)}"></button>`;
        }
        return '';
      }).join('');
      if (!couleur) {
        const id = `CompoOption-${option.nom.replace(/\W+/g, '-')}`;
        const choix = valeurs.map((valeur) => ({ valeur: valeur.v, texte: valeur.v, choisi: etat.choix[option.nom] === valeur.v }));
        return `<div class="product-form__input product-form__input--dropdown option-liste"><div class="form__label flex items-center justify-between gap-2 w-full"><label class="option-liste__titre" for="${id}">${echapper(option.nom)}</label></div>${liste(id, `data-compo-choix-liste="${echapper(option.nom)}"`, choix)}</div>`;
      }
      return `<div class="compo-bar__option"><p class="compo-bar__titre">${echapper(t('couleur', 'Couleur du bar'))} <span class="compo-bar__valeur">· ${echapper(etat.choix[option.nom])}</span></p><div class="compo-bar__pastilles">${boutons}</div></div>`;
    }).join('');
  };

  const afficherTuiles = (c) => {
    $('[data-compo-tuiles]').innerHTML = modules.map((module) => {
      const variante = varianteDe(module);
      const q = etat.q[module.id];
      const image = (variante || module.variantes[0])?.i;
      const prix = variante ? argent(ttc(variante.p) * (100 - c.pct) / 100) : t('indispo', 'Pas dans cette couleur');
      const nom = echapper(module.nom);
      let commande = '';
      if (!variante) commande = '';
      else if (q === 0) commande = `<button type="button" class="compo-bar__ajouter" data-compo-pas="1" data-module="${module.id}">${echapper(t('ajouter', 'Ajouter'))}</button>`;
      else commande = `<div class="compo-bar__pas"><button type="button" data-compo-pas="-1" data-module="${module.id}" aria-label="${echapper(t('retirer_nom', 'Retirer : [nom]', { nom: module.nom }))}">−</button><span>${q}</span><button type="button" data-compo-pas="1" data-module="${module.id}" aria-label="${echapper(t('ajouter_nom', 'Ajouter : [nom]', { nom: module.nom }))}">+</button></div>`;
      return `<div class="compo-bar__tuile${q > 0 && variante ? ' is-prise' : ''}${variante ? '' : ' is-indispo'}">
        <div class="compo-bar__image">${image ? `<img src="${image}" alt="${nom}" loading="lazy" width="200" height="150">` : ''}</div>
        <div class="compo-bar__corps"><span class="compo-bar__nom">${nom}</span><span class="compo-bar__prix-unite">${prix}</span>${commande}</div>
      </div>`;
    }).join('');
  };

  const afficherPlateau = (c) => {
    const bloc_plateau = $('[data-compo-plateau]');
    if (!plateaux.length) return;
    bloc_plateau.hidden = false;
    $('[data-compo-plateau-titre]').textContent = modulePlateau?.plateau.nom || t('plateau', 'Plateau');
    const reference = modulePlateau;
    const choixPlateaux = plateaux.map((plateau) => {
      let cout = 0;
      let inclus = false;
      c.lignes.forEach((ligne) => {
        const choix = ligne.module.plateau?.choix.find((x) => x.libelle === plateau.libelle);
        if (choix?.supp) cout += ligne.q * ttc(choix.p);
        else if (choix?.note) inclus = true;
      });
      let sous;
      if (cout > 0) sous = `+ ${argent(cout)}`;
      else if (!c.lignes.length) {
        const choix = reference?.plateau.choix.find((x) => x.libelle === plateau.libelle);
        sous = choix?.supp ? `+ ${argent(ttc(choix.p))} ${t('par_module', '/ module')}` : (choix?.note || plateau.note || '');
      }
      else sous = inclus ? (plateau.note || t('inclus', 'Inclus')) : '';
      sous = sous.replace(/<[^>]+>/g, '');
      return { valeur: plateau.libelle, texte: sous ? `${plateau.libelle} (${sous})` : plateau.libelle, choisi: plateau.libelle === etat.plateau };
    });
    $('[data-compo-plateaux]').innerHTML = liste('CompoPlateau', 'data-compo-plateau-liste', choixPlateaux);

    const plateau = plateaux.find((x) => x.libelle === etat.plateau);
    const blocTeinte = $('[data-compo-teinte-bloc]');
    $$('[data-compo-nuancier]').forEach((nuancier) => { nuancier.hidden = nuancier.dataset.compoNuancier !== plateau?.nuancier; });
    blocTeinte.hidden = !plateau?.nuancier;
    if (plateau?.nuancier) {
      const teinte = etat.teintes[plateau.nuancier];
      $('[data-compo-teinte-libelle]').textContent = modulePlateau?.plateau.teinte || t('teinte', 'Teinte');
      $('[data-compo-teinte-nom]').textContent = teinte?.teinte || '';
      $$(`[data-compo-nuancier="${plateau.nuancier}"] [data-compo-teinte]`).forEach((pastille) => {
        const choisie = pastille.dataset.compoTeinte === teinte?.teinte;
        pastille.classList.toggle('is-choisie', choisie);
        pastille.setAttribute('aria-pressed', choisie);
      });
      const mention = $('[data-compo-mention]');
      mention.textContent = teinte?.mention || '';
      mention.hidden = !teinte?.mention;
    }
  };

  const afficherCases = (c) => {
    if (!cases.length) return;
    $('[data-compo-cases-bloc]').hidden = false;
    $('[data-compo-cases]').innerHTML = cases.map((option) => {
      const max = piecesPour(c.lignes, option.nom);
      const nombre = etat.cases[option.nom];
      const nom = echapper(option.nom);
      const aide = parBar.includes(option.nom) ? t('un_par_module', '1 par module au plus') : t('kit_par_module', '1 kit par module');
      let commande;
      if (!max) commande = `<span class="compo-bar__case-attente">${echapper(t('ajoutez_module', 'Ajoutez un module'))}</span>`;
      else if (!nombre) commande = `<button type="button" class="compo-bar__ajouter" data-compo-opt="1" data-option="${nom}">${echapper(t('ajouter', 'Ajouter'))}</button>`;
      else commande = `<div class="compo-bar__pas"><button type="button" data-compo-opt="-1" data-option="${nom}" aria-label="${echapper(t('retirer_nom', 'Retirer : [nom]', { nom: option.nom }))}">−</button><span>${nombre}</span><button type="button" data-compo-opt="1" data-option="${nom}" aria-label="${echapper(t('ajouter_nom', 'Ajouter : [nom]', { nom: option.nom }))}"${nombre >= max ? ' disabled' : ''}>+</button></div>`;
      return `<div class="compo-bar__case${nombre ? ' is-prise' : ''}"><span class="compo-bar__case-texte"><span>${nom}</span><small>+ ${argent(ttc(option.p))} · ${aide}</small></span>${commande}</div>`;
    }).join('');
  };

  const afficherRecap = (c) => {
    $('[data-compo-vide]').hidden = c.n > 0;
    $('[data-compo-compte]').textContent = c.n ? nbModules(c.n) : '';
    $('[data-compo-lignes]').innerHTML = c.lignes.map((ligne) => {
      const brut = ligne.q * ligne.unite;
      const barre = c.pct ? `<span class="compo-bar__barre">${argent(brut)}</span> ` : '';
      return `<div class="compo-bar__ligne"><span><span class="compo-bar__qte">${ligne.q} ×</span> ${echapper(ligne.module.nom)}</span><span>${barre}${argent(brut * (100 - c.pct) / 100)}</span></div>`;
    }).join('');
    $('[data-compo-bloc-options]').hidden = !c.options.length;
    $('[data-compo-lignes-options]').innerHTML = c.options.map((option) => `<div class="compo-bar__ligne"><span>${echapper(option.libelle)}</span><span>${argent(option.montant)}</span></div>`).join('');

    const remise = $('[data-compo-remise]');
    remise.hidden = !c.remise;
    if (c.remise) {
      $('[data-compo-remise-libelle]').textContent = t('remise', 'Remise bar −[pct] % (modules)', { pct: c.pct });
      $('[data-compo-remise-montant]').textContent = `−${argent(c.remise).replace(/<[^>]+>/g, '')}`;
    }

    const blocPaliers = $('[data-compo-paliers]');
    blocPaliers.hidden = !paliers.length;
    if (paliers.length) {
      const max = paliers[paliers.length - 1].n;
      $('[data-compo-jauge]').style.width = `${Math.min(100, c.n / max * 100)}%`;
      $('[data-compo-reperes]').innerHTML = paliers.map((palier) => `<span class="compo-bar__cran${c.n >= palier.n ? ' is-atteint' : ''}" style="left:${palier.n / max * 100}%"><span class="compo-bar__cran-point"></span><span class="compo-bar__cran-texte"><strong>−${palier.pct} %</strong>${echapper(nbModules(palier.n))}</span></span>`).join('');
      const suivant = paliers.find((palier) => c.n < palier.n);
      let texte;
      if (!suivant) texte = t('relance_max_html', '<strong>Remise maximale de −[pct] %</strong> appliquée à tout le bar.', { pct: c.pct });
      else if (!c.palier) texte = t('relance_suivant_html', 'Encore <strong>[modules]</strong> pour <strong>−[pct] %</strong> sur tout le bar.', { modules: echapper(nbModules(suivant.n - c.n)), pct: suivant.pct });
      else texte = t('relance_palier_html', '<strong>−[pct] % appliqués.</strong> Encore [modules] pour passer à −[suivant] %.', { pct: c.pct, modules: echapper(nbModules(suivant.n - c.n)), suivant: suivant.pct });
      $('[data-compo-relance]').innerHTML = texte;
    }

    $$('[data-compo-total]', document).forEach((total) => { total.innerHTML = argent(c.total); });
    const barre = $('[data-compo-sous-total]');
    const tauxBadge = $('[data-compo-taux]');
    const sansRemise = c.total + c.remise;
    barre.hidden = !c.remise;
    tauxBadge.hidden = !c.remise;
    const barreRecap = $('[data-compo-recap-barre]');
    const economie = $('[data-compo-economie]');
    barreRecap.hidden = !c.remise;
    economie.hidden = !c.remise;
    if (c.remise) {
      barreRecap.innerHTML = argent(sansRemise);
      economie.innerHTML = t('economie', 'Vous économisez [montant]', { montant: argent(c.remise) });
    }
    if (c.remise) {
      barre.innerHTML = argent(sansRemise);
      tauxBadge.textContent = `−${c.pct} %`;
    }

    $$('[data-compo-ajout]', document).forEach((bouton) => { bouton.disabled = c.n === 0; });
    const compte = document.querySelector('[data-compo-fixe-compte]');
    if (compte) compte.textContent = `${nbModules(c.n)}${c.pct ? ` · −${c.pct} %` : ''} · ${t('voir_detail', 'voir le détail')}`;
  };

  const afficherWhatsapp = (c) => {
    const lien = $('[data-compo-whatsapp]');
    if (!lien) return;
    const lignes = c.lignes.map((ligne) => `${ligne.q} × ${ligne.module.titre}${ligne.variante.titre !== 'Default Title' ? ` (${ligne.variante.titre})` : ''}`);
    const plateau = plateaux.find((x) => x.libelle === etat.plateau);
    if (plateau && c.lignes.some((ligne) => choixPlateau(ligne.module)?.supp || choixPlateau(ligne.module)?.note)) {
      const teinte = plateau.nuancier ? etat.teintes[plateau.nuancier]?.teinte : '';
      lignes.push(`${modulePlateau?.plateau.nom || t('plateau', 'Plateau')} : ${etat.plateau}${teinte ? `, ${teinte}` : ''}`);
    }
    cases.filter((option) => etat.cases[option.nom]).forEach((option) => lignes.push(`${option.nom} × ${etat.cases[option.nom]}`));
    const total = argent(c.total).replace(/<[^>]+>/g, '');
    const totalTexte = t('whatsapp_total', 'Total affiché : [total] [taxe]', { total, taxe: taux ? t('ttc', 'TTC') : t('ht', 'HT') });
    const message = `${t('whatsapp_intro', 'Bonjour, je souhaite composer un bar :')}\n${lignes.join('\n')}\n${totalTexte}\n${bloc.dataset.url}`;
    lien.href = bloc.dataset.numero.replace(/\?text=.*$/, '') + `?text=${encodeURIComponent(message)}`;
  };

  const afficher = () => {
    const focus = document.activeElement && bloc.contains(document.activeElement) ? document.activeElement : null;
    const repere = focus && ['compoPas', 'compoChoix', 'compoChoixListe', 'compoPlateauListe', 'compoOpt'].map((cle) => [cle, focus.dataset[cle]]).find(([, valeur]) => valeur !== undefined);
    const repereModule = focus?.dataset.module || focus?.dataset.option;

    const c = calcul();
    afficherOptionsVariantes();
    afficherTuiles(c);
    afficherPlateau(c);
    afficherCases(c);
    afficherRecap(c);
    afficherWhatsapp(c);
    majBarreFixe();

    // Le bloc est redessiné : le focus revient sur le même bouton.
    if (repere) {
      const [cle, valeur] = repere;
      const attribut = `data-${cle.replace(/[A-Z]/g, (l) => `-${l.toLowerCase()}`)}`;
      let cible = $$(`[${attribut}]`).find((el) => el.dataset[cle] === valeur && (!repereModule || (el.dataset.module || el.dataset.option) === repereModule));
      if (!cible && repereModule) cible = $$('[data-compo-pas], [data-compo-opt]').find((el) => (el.dataset.module || el.dataset.option) === repereModule);
      cible?.focus();
    }
  };

  /* Interactions */

  const choisir = (nom, valeur) => {
    etat.choix[nom] = valeur;
    // Une couleur absente de la nouvelle finition : la première disponible.
    (donnees.options || []).forEach((option) => {
      if (option.nom === nom) return;
      if (!valeurPossible(option.nom, etat.choix[option.nom])) {
        const premiere = option.valeurs.find((v) => valeurPossible(option.nom, v.v));
        if (premiere) etat.choix[option.nom] = premiere.v;
      }
    });
    afficher();
    majGalerie();
  };

  /* Galerie : elle suit la couleur choisie. Même rendu que le sélecteur de variantes des fiches
     (section rechargée pour la variante, puis ProductInfo.updateGalleryFromDestination de theme.js).
     La section et sa première image se chargent dès le survol d'une pastille. */

  const infoProduit = document.querySelector(`product-info[data-product-id="${bloc.dataset.produit}"]`);
  const moduleActuel = modules.find((module) => module.actuel);
  const galerieActive = Boolean(moduleActuel && infoProduit?.updateGalleryFromDestination);
  let varianteGalerie = galerieActive ? varianteDe(moduleActuel)?.id : null;
  let tourGalerie = 0;
  const sectionsGalerie = new Map();
  const chargerGalerie = (id) => {
    if (!sectionsGalerie.has(id)) {
      // Sur un thème d'aperçu, les sous-dossiers de langue perdent l'aperçu sans son identifiant.
      const apercu = window.Shopify?.theme && window.Shopify.theme.role !== 'main' ? `&preview_theme_id=${window.Shopify.theme.id}` : '';
      sectionsGalerie.set(id, fetch(`${window.location.pathname}?variant=${id}&section_id=${infoProduit.sectionId}${apercu}`)
        .then((reponse) => {
          if (!reponse.ok) throw new Error(`Galerie : ${reponse.status}`);
          return reponse.text();
        })
        .then((html) => {
          const page = new DOMParser().parseFromString(html, 'text/html');
          const image = page.querySelector('[id^="ProductGallery-"] [data-media-id] img');
          if (image) {
            const avance = new Image();
            avance.sizes = image.getAttribute('sizes') || '';
            avance.srcset = image.getAttribute('srcset') || '';
            avance.src = image.getAttribute('src') || '';
          }
          return page;
        })
        .catch((error) => {
          sectionsGalerie.delete(id);
          throw error;
        }));
    }
    return sectionsGalerie.get(id);
  };
  const majGalerie = async () => {
    const variante = galerieActive ? varianteDe(moduleActuel) : null;
    if (!variante || variante.id === varianteGalerie) return;
    varianteGalerie = variante.id;
    const tour = ++tourGalerie;
    const adresse = new URL(window.location.href);
    adresse.searchParams.set('variant', variante.id);
    window.history.replaceState({ path: adresse.toString() }, '', adresse.toString());
    try {
      const page = await chargerGalerie(variante.id);
      if (tour === tourGalerie) infoProduit.updateGalleryFromDestination(page);
    } catch (error) {
      console.log(error);
    }
  };
  bloc.addEventListener('pointerover', (event) => {
    const pastille = event.target.closest('[data-compo-choix]');
    if (!pastille || !galerieActive) return;
    const variante = varianteDe(moduleActuel, { ...etat.choix, [pastille.dataset.compoChoix]: pastille.dataset.valeur });
    if (variante && variante.id !== varianteGalerie) chargerGalerie(variante.id).catch(() => {});
  });

  bloc.addEventListener('click', (event) => {
    const pas = event.target.closest('[data-compo-pas]');
    if (pas) {
      const id = pas.dataset.module;
      etat.q[id] = Math.max(0, Math.min(20, (etat.q[id] || 0) + parseInt(pas.dataset.compoPas, 10)));
      afficher();
      return;
    }
    const opt = event.target.closest('[data-compo-opt]');
    if (opt) {
      const nom = opt.dataset.option;
      const max = piecesPour(calcul().lignes, nom);
      etat.cases[nom] = Math.max(0, Math.min(max, etat.cases[nom] + parseInt(opt.dataset.compoOpt, 10)));
      afficher();
      return;
    }
    const choix = event.target.closest('[data-compo-choix]');
    if (choix) {
      choisir(choix.dataset.compoChoix, choix.dataset.valeur);
      return;
    }
    const teinte = event.target.closest('[data-compo-teinte]');
    if (teinte) {
      const nuancier = teinte.closest('[data-compo-nuancier]').dataset.compoNuancier;
      etat.teintes[nuancier] = { teinte: teinte.dataset.compoTeinte, code: teinte.dataset.code, mention: teinte.dataset.mention };
      afficher();
    }
  });

  bloc.addEventListener('change', (event) => {
    const listeOption = event.target.closest('[data-compo-choix-liste]');
    if (listeOption) {
      choisir(listeOption.dataset.compoChoixListe, listeOption.value);
      return;
    }
    const listePlateau = event.target.closest('[data-compo-plateau-liste]');
    if (listePlateau) {
      etat.plateau = listePlateau.value;
      afficher();
    }
  });

  /* Lignes du bar : panier et devis */

  const lignesDuBar = () => {
    const lignes = [];
    const supplements = {};
    const ajouterSupplement = (id, q) => { supplements[id] = (supplements[id] || 0) + q; };

    modulesPris().forEach((module) => {
      const variante = varianteDe(module);
      const q = etat.q[module.id];
      const proprietes = {};
      const visibles = [];
      const supps = [];
      const choix = choixPlateau(module);
      if (choix && (choix.supp || choix.note)) {
        proprietes[module.plateau.nom] = choix.libelle;
        const teinte = choix.nuancier ? etat.teintes[choix.nuancier] : null;
        visibles.push({ name: module.plateau.nom, value: choix.libelle, code: '' });
        if (teinte) {
          proprietes[module.plateau.teinte] = teinte.teinte;
          proprietes[`_Code ${module.plateau.teinte.toLowerCase()}`] = teinte.code;
          visibles.push({ name: module.plateau.teinte, value: teinte.teinte, code: teinte.code });
        }
        if (choix.supp) {
          proprietes[`_Supplément ${module.plateau.nom}`] = String(choix.supp);
          supps.push({ id: choix.supp, p: choix.p, url: choix.url });
        }
      }
      lignes.push({ module, variante, q, proprietes, visibles, supps });
    });

    // Chaque option est portée par autant de pièces que son compteur, dans l'ordre des modules :
    // une ligne est coupée en deux si seule une partie de ses pièces la prend.
    cases.forEach(({ nom }) => {
      let reste = etat.cases[nom];
      lignes.filter((l) => l.module.cases.some((option) => option.nom === nom)).forEach((ligne) => {
        if (reste <= 0) return;
        const option = ligne.module.cases.find((x) => x.nom === nom);
        const prises = Math.min(reste, ligne.q);
        reste -= prises;
        let cible = ligne;
        if (prises < ligne.q) {
          ligne.q -= prises;
          cible = { ...ligne, q: prises, proprietes: { ...ligne.proprietes }, visibles: [...ligne.visibles], supps: [...ligne.supps] };
          lignes.splice(lignes.indexOf(ligne), 0, cible);
        }
        cible.proprietes[nom] = option.valeur;
        cible.proprietes[`_Supplément ${nom}`] = String(option.supp);
        cible.visibles.push({ name: nom, value: option.valeur, code: '' });
        cible.supps.push({ id: option.supp, p: option.p, url: option.url });
      });
    });

    lignes.forEach((ligne) => ligne.supps.forEach((supp) => ajouterSupplement(supp.id, ligne.q)));
    return { lignes, supplements };
  };

  const erreur = (texte) => {
    const zone = $('[data-compo-erreur]');
    zone.textContent = texte || '';
    zone.hidden = !texte;
  };

  const ajouterAuPanier = async (bouton) => {
    const { lignes, supplements } = lignesDuBar();
    if (!lignes.length) return;
    erreur('');
    $$('[data-compo-ajout]', document).forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      const items = [
        ...lignes.map((ligne) => ({ id: ligne.variante.id, quantity: ligne.q, properties: ligne.proprietes })),
        ...Object.entries(supplements).map(([id, quantity]) => ({ id: parseInt(id, 10), quantity }))
      ];
      const reponse = await fetch(`${theme.routes.cart_add_url}.js`, { ...theme.utils.fetchConfig('json'), body: JSON.stringify({ items }) });
      const resultat = await reponse.json();
      if (!reponse.ok) {
        erreur(resultat.description || resultat.message || t('erreur', 'Le bar n’a pas pu être ajouté au panier.'));
        return;
      }
      const palier = palierDe(nombre());
      if (palier?.code && window.veranoPoserCode) await window.veranoPoserCode(palier.code);

      const panier = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
      theme.pubsub?.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'composition-bar', cart: panier });
      if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      }
      else {
        window.location.href = theme.routes.cart_url;
      }
    } catch (error) {
      console.log(error);
      erreur(t('erreur', 'Le bar n’a pas pu être ajouté au panier.'));
    } finally {
      $$('[data-compo-ajout]', document).forEach((b) => b.removeAttribute('aria-busy'));
    }
  };

  const ajouterAuDevis = (bouton) => {
    if (!window.veranoDevis) return;
    const { lignes } = lignesDuBar();
    const origine = window.location.origin;
    window.veranoDevis.ajouter(lignes.map((ligne) => ({
      id: ligne.variante.id,
      options: ligne.visibles,
      product: ligne.module.titre,
      variant: ligne.variante.titre !== 'Default Title' ? ligne.variante.titre : '',
      quantity: ligne.q,
      lotSize: 0,
      lotPlural: '',
      image: ligne.variante.i,
      url: `${origine}${ligne.module.url}?variant=${ligne.variante.id}`,
      price: ttc(ligne.variante.p) + ligne.supps.reduce((total, supp) => total + ttc(supp.p), 0),
      taux: tauxReel,
      supplements: ligne.supps.map((supp) => ({ id: supp.id, url: supp.url }))
    })));
    window.veranoDevis.ouvrir(bouton);
  };

  document.addEventListener('click', (event) => {
    const ajout = event.target.closest('[data-compo-ajout]');
    if (ajout) {
      ajouterAuPanier(ajout);
      return;
    }
    const devis = event.target.closest('[data-compo-devis]');
    if (devis) {
      ajouterAuDevis(devis);
      return;
    }
    const resume = event.target.closest('[data-compo-fixe-resume]');
    if (resume) {
      event.preventDefault();
      const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      document.getElementById('composition-bar-recap')?.scrollIntoView({ behavior: doux ? 'smooth' : 'auto', block: 'start' });
    }
  });

  /* Barre fixe (mobile) : déplacée sous <body>, la colonne produit a un transform qui
     enfermerait une position fixe. Masquée tant que le bouton d'ajout du bloc est à l'écran. */

  const barre = $('[data-compo-fixe]');
  let actionsVisibles = false;
  if (barre) document.body.appendChild(barre);
  function majBarreFixe() {
    if (!barre) return;
    const montrer = nombre() > 0 && !actionsVisibles;
    barre.hidden = !montrer;
    document.body.classList.toggle('compo-bar-fixe-visible', montrer);
  }
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entrees) => {
      actionsVisibles = entrees.some((entree) => entree.isIntersecting);
      majBarreFixe();
    }, { rootMargin: '0px 0px -64px 0px' }).observe($('.compo-bar__actions [data-compo-ajout]'));
  }

  afficher();
})();
